import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '@mode/capra';
import { ChartTypes } from '@mode/shared/contract-common';
import { LabeledMenuComponent } from '@mode/shared/ui';
import * as TextFormattingHelpers from '../text-formatting-helpers';

export const BASE_FORMAT: ChartTypes.TextFormat.PercentType = {
  ...TextFormattingHelpers.getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.Percent),
};

@Component({
  standalone: true,
  selector: 'mode-percent-format-pane',
  templateUrl: './percent-format-pane.component.html',
  styleUrls: ['./percent-format-pane.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, IconComponent, LabeledMenuComponent],
})
export class PercentFormatPaneComponent implements OnChanges {
  @Input({ required: true }) format!: ChartTypes.TextFormat.PercentType;
  @Output() formatChange = new EventEmitter<ChartTypes.TextFormat.PercentType>();

  readonly thousandsSeparatorMenuItems = TextFormattingHelpers.ThousandsSeparatorMenuItems;
  readonly thousandsGroupingMenuItems = TextFormattingHelpers.ThousandsGroupingMenuItems;
  readonly decimalSymbolMenuItems = TextFormattingHelpers.DecimalSeparatorMenuItems;

  selectedThousandsSeparator?: TextFormattingHelpers.ThousandsSeparatorMenuItem;
  selectedThousandsGrouping?: TextFormattingHelpers.ThousandsGroupingMenuItem;
  selectedDecimalSymbol?: TextFormattingHelpers.DecimalSeparatorMenuItem;

  ngOnChanges() {
    const format =
      // Mix in the BASE_FORMAT such that we always have
      // the minimum set of properties per the format type
      (this.format = {
        ...BASE_FORMAT,
        ...this.format,
      });

    this.selectedThousandsSeparator = this.thousandsSeparatorMenuItems.find((opt) => format.separator === opt.value);
    this.selectedThousandsGrouping = this.thousandsGroupingMenuItems.find((opt) => format.grouping === opt.value);
    this.selectedDecimalSymbol = this.decimalSymbolMenuItems.find((opt) => format.decimalStyle === opt.value);
  }

  apply(props: Partial<ChartTypes.TextFormat.PercentType>) {
    this.format = { ...this.format, ...props };
    this.formatChange.emit(this.format);
  }

  onChangeSeparator(item: TextFormattingHelpers.ThousandsSeparatorMenuItem) {
    this.selectedThousandsSeparator = item;
    this.apply({
      separator: item.value,
    });
  }

  onChangeGrouping(item: TextFormattingHelpers.ThousandsGroupingMenuItem) {
    this.selectedThousandsGrouping = item;
    this.apply({
      grouping: item.value,
    });
  }

  onChangeSymbol(item: TextFormattingHelpers.DecimalSeparatorMenuItem) {
    this.selectedDecimalSymbol = item;
    this.apply({
      decimalStyle: item.value,
    });
  }

  onChangePrecision(value: unknown) {
    const precision = TextFormattingHelpers.isValidPrecision(value) ? value : BASE_FORMAT.precision;
    this.apply({
      precision,
    });
  }
}
